import "./lifts.css";
import type { KeyboardEvent, MouseEvent, ReactNode } from "react";
import { useParams } from "react-router";
import { PageHeader, SidePanel, Sparkline, Status, Table, byNumber, byText, type Column, type Sort } from "../../kit/index.js";
import { useModel, usePortal, useTitle, type LiftSort } from "../context.js";
import { errorMessage, liftsKey, loadLifts, useResource } from "../data.js";
import { dayShort, monthDay } from "../model/days.js";
import { changeText, heavier, kg, liftSetText, standing, type Standing, type TeamLift } from "../model/lifts.js";
import { plural } from "../model/plan.js";
import { fold } from "../model/text.js";
import type { LiftSet } from "../model/types.js";
import { plannedLifts, type Athlete, type WeekModel } from "../model/week.js";
import { PrBadge } from "../parts.js";
import { href } from "../paths.js";

const CONTEXT = "Every lift done with a weight in the last 26 weeks";

interface LiftRow {
  key: string;
  name: string;
  planned: boolean;
  athletes: number;
  top: LiftSet | null;
  last: string | null;
}

function setsIn(lift: TeamLift, model: WeekModel, column: string | null): Map<string, LiftSet[]> {
  if (!column) return lift.athletes;
  const out = new Map<string, LiftSet[]>();
  lift.athletes.forEach((sets, id) => {
    if (model.athletes.find((a) => a.id === id)?.column === column) out.set(id, sets);
  });
  return out;
}

function rowOf(key: string, name: string, planned: boolean, lift: TeamLift | undefined, model: WeekModel, column: string | null): LiftRow {
  const sets = lift ? setsIn(lift, model, column) : new Map<string, LiftSet[]>();
  let top: LiftSet | null = null;
  let last: string | null = null;
  for (const list of sets.values())
    for (const x of list) {
      if (!top || heavier(x, top)) top = x;
      if (!last || x.date > last) last = x.date;
    }
  return { key, name: lift?.name ?? name, planned, athletes: sets.size, top, last };
}

function nameOf(model: WeekModel, id: string): string | null {
  return model.athletes.find((a) => a.id === id)?.name ?? null;
}

function liftColumns(model: WeekModel, column: string | null): Column<LiftRow>[] {
  return [
    {
      key: "name",
      label: "Lift",
      sticky: true,
      sort: byText((r) => r.name),
      render: (r) => (
        <span className="lift-name">
          <span>{r.name}</span>
          {r.planned ? <Status kind="planned" text="In this week’s plan" /> : null}
        </span>
      ),
    },
    {
      key: "athletes",
      label: "Athletes",
      className: "k-hide-phone",
      align: "end",
      sort: byNumber((r) => r.athletes),
      render: (r) => (r.athletes ? <span className="k-num">{r.athletes}</span> : <span className="k-muted">{column ? "None in " + column + " yet" : "No sets shared yet"}</span>),
    },
    {
      key: "heaviest",
      label: "Team’s heaviest",
      sort: byNumber((r) => (r.top ? kg(r.top) : -Infinity)),
      render: (r) => {
        if (!r.top) return null;
        const who = nameOf(model, r.top.athleteID);
        return (
          <span className="k-num">
            <b>{liftSetText(r.top)}</b>
            <span className="k-muted">{(who ? " · " + who : "") + " · " + dayShort(r.top.date)}</span>
          </span>
        );
      },
    },
    {
      key: "last",
      label: "Last done",
      className: "k-hide-phone",
      sort: byText((r) => r.last ?? ""),
      render: (r) => (r.last ? <span className="k-num">{dayShort(r.last)}</span> : null),
    },
  ];
}

interface AthleteRow {
  a: Athlete;
  st: Standing;
}

function heaviestOrder(x: AthleteRow, y: AthleteRow): number {
  return heavier(x.st.heaviest, y.st.heaviest) ? 1 : heavier(y.st.heaviest, x.st.heaviest) ? -1 : 0;
}

const athleteColumns: Column<AthleteRow>[] = [
  {
    key: "name",
    label: "Athlete",
    sticky: true,
    sort: byText((r) => r.a.name),
    render: ({ a }) => (
      <span data-athlete={a.id}>
        {a.name}
        {a.column ? <span className="k-sub">{a.column}</span> : null}
      </span>
    ),
  },
  {
    key: "heaviest",
    label: "Heaviest",
    align: "end",
    sort: heaviestOrder,
    render: ({ st }) => (
      <span className="k-num">
        <b>{liftSetText(st.heaviest)}</b>
        <span className="k-sub">
          {st.heaviest.record ? (
            <>
              <PrBadge text={st.heaviest.record} />{" "}
            </>
          ) : null}
          {dayShort(st.heaviest.date)}
        </span>
      </span>
    ),
  },
  {
    key: "bests",
    label: "Best at each rep count",
    className: "k-hide-phone",
    wrap: true,
    render: ({ st }) => (
      <span className="lift-bests">
        {st.bests.map((x, i) => {
          const top = x === st.heaviest;
          return (
            <span key={i} className={"lift-best k-num" + (top ? " is-top" : "")}>
              {liftSetText(x)}
              {top ? <span className="sr">, heaviest</span> : null}
            </span>
          );
        })}
      </span>
    ),
  },
  {
    key: "latest",
    label: "Latest",
    className: "k-hide-phone",
    align: "end",
    sort: byText((r) => r.st.latest.date),
    render: ({ st }) => (
      <span className="k-num">
        {liftSetText(st.latest)}
        <span className="k-sub">{dayShort(st.latest.date)}</span>
      </span>
    ),
  },
  {
    key: "change",
    label: "Most improved",
    sort: byNumber((r) => r.st.change ?? -Infinity),
    render: ({ st }) => <Change st={st} />,
  },
];

function Change({ st }: { st: Standing }) {
  const change = changeText(st);
  const values = st.recent.map((p) => p.kg);
  const min = Math.min(...values);
  const span = Math.max(...values) - min || 1;
  return (
    <span className="lift-change">
      {values.length > 1 ? <Sparkline values={values.map((v) => v - min + span * 0.2)} width={48} height={18} /> : null}
      {change ? (
        <span className="k-num" title={"Since " + monthDay(st.since!)}>
          {change}
          <span className="sr"> since {monthDay(st.since!)}</span>
        </span>
      ) : (
        <span className="k-muted">One week</span>
      )}
    </span>
  );
}

const SORTS: Record<LiftSort, Sort> = {
  heaviest: { key: "heaviest", dir: "desc" },
  improved: { key: "change", dir: "desc" },
  name: { key: "name", dir: "asc" },
};

function LiftPanel({ liftKey, lift, plannedName, column }: { liftKey: string; lift: TeamLift | undefined; plannedName: string | undefined; column: string | null }) {
  const model = useModel();
  const { ui, pickLift } = usePortal();
  const closeTo = href(model.monday, "lifts");
  const name = lift?.name ?? plannedName ?? liftKey;
  if (!lift)
    return (
      <SidePanel title={name} closeTo={closeTo}>
        <p className="k-muted">Nobody has shared a weighed set of it in the last 26 weeks.</p>
      </SidePanel>
    );

  const rows: AthleteRow[] = [];
  setsIn(lift, model, column).forEach((sets, id) => {
    const a = model.athletes.find((x) => x.id === id);
    if (a) rows.push({ a, st: standing(sets, model.today) });
  });

  const pickFrom = (target: EventTarget) => {
    const id = (target as Element).closest?.("tr")?.querySelector("[data-athlete]")?.getAttribute("data-athlete");
    if (id) pickLift(id, lift.key);
  };
  const onClick = (e: MouseEvent) => pickFrom(e.target);
  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Enter") pickFrom(e.target);
  };

  return (
    <SidePanel title={name} subtitle={plural(rows.length, "athlete", "athletes") + (column ? " in " + column : "") + " · last done " + dayShort(lift.last)} closeTo={closeTo} wide>
      <div className="lift-athletes" onClickCapture={onClick} onKeyDownCapture={onKey}>
        <Table
          label={name + ", by athlete"}
          rows={rows}
          columns={athleteColumns}
          rowKey={(r) => r.a.id}
          href={(r) => href(model.monday, "athlete", r.a.id)}
          initialSort={SORTS[ui.liftSort]}
          empty={"Nobody in " + (column || "the team") + " has shared a set of " + name + "."}
        />
      </div>
      <p className="lift-footnote">
        Heaviest is their heaviest set in the last 26 weeks. Best at each rep count lists the heaviest set for each number of reps that nothing heavier beat at more
        reps. Most improved is the change in their weekly heaviest over the last 8 weeks. Open an athlete for this lift’s chart.
      </p>
    </SidePanel>
  );
}

export function Lifts() {
  const model = useModel();
  const { ui, setUI } = usePortal();
  const { key } = useParams();
  const r = useResource(liftsKey, loadLifts);
  const lifts = r.value;
  const planned = plannedLifts(model);
  const current = key ? lifts?.byKey.get(key) : undefined;
  useTitle(key ? (current?.name ?? planned.find((p) => p.key === key)?.name ?? "Lifts") : "Lifts");

  const columns = model.personalColumns;
  const column = ui.liftColumn && columns.includes(ui.liftColumn) ? ui.liftColumn : null;
  const actions = (
    <>
      <span className="k-search">
        <svg viewBox="0 0 16 16" aria-hidden="true">
          <circle cx="7" cy="7" r="4.5" fill="none" stroke="currentColor" strokeWidth="1.6" />
          <path d="m10.4 10.4 3.1 3.1" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
        <input
          type="search"
          className="k-input"
          placeholder="Search lifts"
          aria-label="Search lifts"
          autoComplete="off"
          value={ui.liftQuery}
          onChange={(e) => setUI({ liftQuery: e.target.value })}
        />
      </span>
      {columns.length > 1 ? (
        <select className="k-input" aria-label="Column" value={column ?? ""} onChange={(e) => setUI({ liftColumn: e.target.value || null })}>
          <option value="">All columns</option>
          {columns.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      ) : null}
    </>
  );

  let body: ReactNode;
  if (r.error)
    body = (
      <p className="k-empty" role="alert">
        {errorMessage(r.error)}
      </p>
    );
  else if (!lifts)
    body = (
      <p className="k-empty" role="status">
        Loading the team’s lifts…
      </p>
    );
  else {
    const q = fold(ui.liftQuery || "");
    const matches = (name: string) => !q || fold(name).includes(q);
    const plannedKeys = new Set(planned.map((p) => p.key));
    const rows: LiftRow[] = [
      ...planned.map((p) => rowOf(p.key, p.name, true, lifts.byKey.get(p.key), model, column)),
      ...lifts.list.filter((l) => !plannedKeys.has(l.key)).map((l) => rowOf(l.key, l.name, false, l, model, column)),
    ].filter((row) => matches(row.name) && (row.planned || row.athletes > 0));
    body = (
      <Table
        label="Lifts"
        rows={rows}
        columns={liftColumns(model, column)}
        rowKey={(row) => row.key}
        href={(row) => (row.athletes ? href(model.monday, "lift", row.key) : "")}
        selected={key ?? null}
        rowClass={(row) => (row.athletes ? undefined : "is-dim")}
        empty={lifts.list.length || planned.length ? "No lift matches." : "No weighed sets shared yet."}
      />
    );
  }

  return (
    <div className="k-page">
      <PageHeader title="Lifts" context={CONTEXT} actions={actions} />
      {body}
      {key && lifts ? <LiftPanel liftKey={key} lift={current} plannedName={planned.find((p) => p.key === key)?.name} column={column} /> : null}
    </div>
  );
}
